/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Divider, List, Typography } from '@mui/material';
import React from 'react';
import type { DebugLogEntry } from '../pluginDebugTypes';
import type { EntryRowVariant } from './EntryRow';
import { EntryRow } from './EntryRow';

interface EntryListProps {
  entries: DebugLogEntry[];
  title: React.ReactNode;
  variant: EntryRowVariant;
  containerTestId: string;
  entryTestIdPrefix: string;
  emptyText?: string;
}

export const EntryList: React.FC<EntryListProps> = ({
  entries,
  title,
  variant,
  containerTestId,
  entryTestIdPrefix,
  emptyText = 'No activity recorded yet.',
}) => {
  const titleSx = variant === 'activity' ? { px: 2, pt: 1 } : { px: 2, py: 1 };
  const containerSx = variant === 'recent' ? { px: 2, py: 1 } : undefined;

  return (
    <Box data-testid={containerTestId} sx={containerSx}>
      <Typography
        gutterBottom={variant === 'recent'}
        sx={titleSx}
        variant="subtitle2"
      >
        {title}
      </Typography>
      {entries.length === 0 ? (
        <Typography
          color="text.secondary"
          sx={variant === 'activity' ? { padding: 2 } : undefined}
          variant="body2"
        >
          {emptyText}
        </Typography>
      ) : (
        <List dense disablePadding>
          {entries.map((entry) => (
            <React.Fragment key={entry.id}>
              <EntryRow
                entry={entry}
                testId={`${entryTestIdPrefix}-${entry.id}`}
                variant={variant}
              />
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};
